<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BarChart from 'shared/components/charts/BarChart.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import KanbanReportWidget from 'kanban/components/reports/KanbanReportWidget.vue';
import { formatPeriodLabel } from 'kanban/helper/reportFormatters';
import {
  TIMESERIES_METRICS,
  TIMESERIES_GROUP_BY,
  CHART_PRIMARY_COLOR,
} from 'kanban/constants/reports';

const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  metric: {
    type: String,
    default: 'throughput',
  },
  groupBy: {
    type: String,
    default: 'day',
  },
  locale: {
    type: String,
    default: 'en',
  },
});

const emit = defineEmits(['retry', 'update:metric', 'update:groupBy']);

const { t } = useI18n();

const metricLabel = key =>
  t(`KANBAN.REPORTS.THROUGHPUT.METRIC.${key.toUpperCase()}`);
const groupByLabel = key =>
  t(`KANBAN.REPORTS.THROUGHPUT.GROUP_BY.${key.toUpperCase()}`);

const metricOptions = computed(() =>
  TIMESERIES_METRICS.map(value => ({ value, label: metricLabel(value) }))
);
const groupByOptions = computed(() =>
  TIMESERIES_GROUP_BY.map(value => ({ value, label: groupByLabel(value) }))
);

const points = computed(() => props.data?.data || []);

const isEmpty = computed(
  () => !points.value.length || points.value.every(point => !point.value)
);

const labelFor = isoDate =>
  formatPeriodLabel(isoDate, props.groupBy, props.locale);

const collection = computed(() => ({
  labels: points.value.map(point => labelFor(point.date)),
  datasets: [
    {
      label: metricLabel(props.metric),
      data: points.value.map(point => point.value),
      backgroundColor: CHART_PRIMARY_COLOR,
      borderRadius: 4,
    },
  ],
}));
</script>

<template>
  <KanbanReportWidget
    :title="t('KANBAN.REPORTS.THROUGHPUT.TITLE')"
    :info-text="t('KANBAN.REPORTS.THROUGHPUT.INFO')"
    :is-loading="isLoading"
    :error="error"
    :is-empty="isEmpty"
    @retry="emit('retry')"
  >
    <template #actions>
      <SelectMenu
        :model-value="metric"
        :options="metricOptions"
        :label="metricLabel(metric)"
        max-width="max-w-48"
        sub-menu-position="bottom"
        @update:model-value="emit('update:metric', $event)"
      />
      <SelectMenu
        :model-value="groupBy"
        :options="groupByOptions"
        :label="groupByLabel(groupBy)"
        max-width="max-w-36"
        sub-menu-position="bottom"
        @update:model-value="emit('update:groupBy', $event)"
      />
    </template>
    <div class="h-72">
      <BarChart :collection="collection" />
    </div>
  </KanbanReportWidget>
</template>
